<template>
  <div class="bg-gray-50 min-h-full">
    <div class="space-y-4">
      <!-- 统计卡片 -->
      <div class="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div class="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
          <div class="text-2xl font-bold text-blue-600">{{ stats.scheduled || 0 }}</div>
          <div class="text-sm text-gray-500 mt-1">待发送</div>
        </div>
        <div class="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
          <div class="text-2xl font-bold text-amber-600">{{ stats.processing || 0 }}</div>
          <div class="text-sm text-gray-500 mt-1">发送中</div>
        </div>
        <div class="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
          <div class="text-2xl font-bold text-green-600">{{ stats.completed || 0 }}</div>
          <div class="text-sm text-gray-500 mt-1">已完成</div>
        </div>
        <div class="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
          <div class="text-2xl font-bold text-red-600">{{ stats.failed || 0 }}</div>
          <div class="text-sm text-gray-500 mt-1">失败</div>
        </div>
      </div>

      <!-- 筛选区 -->
      <div class="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
        <div class="flex flex-col lg:flex-row gap-3">
          <div class="flex-1 relative">
            <input
              v-model="searchQuery"
              type="text"
              placeholder="搜索主题、收件人..."
              class="w-full h-10 pl-10 pr-4 text-sm bg-gray-50/50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
              @keyup.enter="loadTasks(1)"
            >
            <MagnifyingGlassIcon class="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
          </div>
          <button
            @click="loadTasks(1)"
            :disabled="loading"
            class="h-10 px-4 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 disabled:opacity-50"
          >
            搜索
          </button>
        </div>

        <!-- 状态 Tab -->
        <div class="flex flex-wrap gap-2 mt-4">
          <button
            v-for="tab in statusTabs"
            :key="tab.key"
            @click="switchTab(tab.key)"
            :class="activeTab === tab.key
              ? 'bg-blue-600 text-white border-blue-600'
              : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'"
            class="px-3 py-1.5 text-sm font-medium border rounded-lg transition-colors"
          >
            {{ tab.label }}
          </button>
        </div>
      </div>

      <!-- 批量操作 -->
      <div
        v-if="selectedIds.length > 0 && activeTab === 'scheduled'"
        class="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 flex items-center justify-between"
      >
        <span class="text-sm text-gray-700">已选择 {{ selectedIds.length }} 个任务</span>
        <div class="flex gap-2">
          <button
            @click="selectedIds = []"
            class="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800"
          >
            取消选择
          </button>
          <button
            @click="confirmBatchCancel"
            class="px-3 py-1.5 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700"
          >
            批量取消
          </button>
        </div>
      </div>

      <!-- 列表 -->
      <div class="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
        <div v-if="loading" class="flex items-center justify-center py-20">
          <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>

        <div v-else-if="tasks.length === 0" class="text-center py-20">
          <ClockIcon class="mx-auto h-12 w-12 text-gray-300 mb-3" />
          <p class="text-gray-500 text-sm">暂无{{ currentTabLabel }}任务</p>
        </div>

        <div v-else class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th v-if="activeTab === 'scheduled'" class="px-4 py-3 w-10">
                  <input
                    type="checkbox"
                    :checked="isAllSelected"
                    @change="toggleSelectAll"
                    class="h-4 w-4 text-blue-600 rounded border-gray-300"
                  >
                </th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">主题</th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">收件人</th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">发件账户</th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">计划时间</th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">状态</th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">创建时间</th>
                <th class="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">操作</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-100">
              <tr
                v-for="task in tasks"
                :key="task.id"
                class="hover:bg-gray-50 transition-colors"
              >
                <td v-if="activeTab === 'scheduled'" class="px-4 py-3">
                  <input
                    type="checkbox"
                    :value="task.id"
                    v-model="selectedIds"
                    class="h-4 w-4 text-blue-600 rounded border-gray-300"
                  >
                </td>
                <td class="px-4 py-3">
                  <div class="text-sm font-medium text-gray-900 max-w-xs truncate" :title="task.subject">
                    {{ task.subject || '(无主题)' }}
                  </div>
                  <div v-if="task.original_email_subject" class="text-xs text-gray-400 truncate max-w-xs">
                    回复: {{ task.original_email_subject }}
                  </div>
                </td>
                <td class="px-4 py-3 text-sm text-gray-600 max-w-[160px] truncate" :title="task.recipient_list">
                  {{ task.recipient_list }}
                </td>
                <td class="px-4 py-3 text-sm text-gray-600 max-w-[140px] truncate">
                  {{ task.send_account_email }}
                </td>
                <td class="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
                  <div>{{ formatDateTime(task.scheduled_at) }}</div>
                  <div v-if="task.status === 'scheduled' && task.scheduled_at" class="text-xs text-blue-500">
                    {{ getCountdown(task.scheduled_at) }}
                  </div>
                </td>
                <td class="px-4 py-3">
                  <span :class="getStatusClass(task.status)" class="inline-flex px-2 py-0.5 text-xs font-medium rounded-full">
                    {{ task.status_display }}
                  </span>
                  <div v-if="task.error_message" class="text-xs text-red-500 mt-1 max-w-[120px] truncate" :title="task.error_message">
                    {{ task.error_message }}
                  </div>
                </td>
                <td class="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
                  {{ formatDateTime(task.created_at) }}
                </td>
                <td class="px-4 py-3 text-right whitespace-nowrap">
                  <button
                    v-if="task.is_editable"
                    @click="openEditModal(task)"
                    class="text-sm text-blue-600 hover:text-blue-800 mr-3"
                  >
                    编辑
                  </button>
                  <button
                    v-if="task.is_cancellable"
                    @click="confirmCancel(task)"
                    class="text-sm text-red-600 hover:text-red-800"
                  >
                    取消
                  </button>
                  <button
                    v-if="task.status === 'failed'"
                    @click="openResendModal(task)"
                    class="text-sm text-blue-600 hover:text-blue-800"
                  >
                    重发
                  </button>
                  <span
                    v-if="!task.is_editable && !task.is_cancellable && task.status !== 'failed'"
                    class="text-xs text-gray-400"
                  >
                    —
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- 分页 -->
        <div v-if="totalPages > 1" class="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
          <span class="text-sm text-gray-500">共 {{ totalCount }} 条</span>
          <div class="flex items-center gap-2">
            <button
              @click="loadTasks(currentPage - 1)"
              :disabled="currentPage <= 1"
              class="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
            >
              上一页
            </button>
            <span class="text-sm text-gray-600">{{ currentPage }} / {{ totalPages }}</span>
            <button
              @click="loadTasks(currentPage + 1)"
              :disabled="currentPage >= totalPages"
              class="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
            >
              下一页
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- 编辑弹窗 -->
    <EmailReplyModal
      :show-modal="showEditModal"
      :edit-reply-log-id="editingTaskId"
      :is-reply="false"
      @close="closeEditModal"
      @updated="onTaskUpdated"
    />

    <!-- 重发弹窗 -->
    <EmailReplyModal
      :show-modal="showResendModal"
      :resend-reply-log-id="resendTaskId"
      :is-reply="false"
      @close="closeResendModal"
      @sent="onResendSent"
    />

    <!-- 取消确认 -->
    <div v-if="showCancelConfirm" class="fixed inset-0 z-50 flex items-center justify-center">
      <div class="fixed inset-0 bg-black/50" @click="showCancelConfirm = false"></div>
      <div class="relative bg-white rounded-lg shadow-xl p-6 max-w-sm w-full mx-4">
        <h3 class="text-lg font-medium text-gray-900 mb-2">确认取消</h3>
        <p class="text-sm text-gray-600 mb-6">
          {{ cancelTarget?.batch
            ? `确定取消选中的 ${selectedIds.length} 个定时任务？取消后不可恢复。`
            : `确定取消定时邮件「${cancelTarget?.subject || ''}」？取消后不可恢复。`
          }}
        </p>
        <div class="flex justify-end gap-3">
          <button
            @click="showCancelConfirm = false"
            class="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            返回
          </button>
          <button
            @click="executeCancel"
            :disabled="cancelling"
            class="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50"
          >
            {{ cancelling ? '取消中...' : '确认取消' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useToast } from 'vue-toastification'
import {
  MagnifyingGlassIcon,
  ClockIcon
} from '@heroicons/vue/24/outline'
import emailServiceV2 from '@/services/emailServiceV2'
import EmailReplyModal from '@/components/EmailReplyModal.vue'

const toast = useToast()

const loading = ref(false)
const tasks = ref([])
const searchQuery = ref('')
const activeTab = ref('scheduled')
const selectedIds = ref([])
const currentPage = ref(1)
const totalCount = ref(0)
const totalPages = ref(1)
const pageSize = 20

const showEditModal = ref(false)
const editingTaskId = ref(null)
const showResendModal = ref(false)
const resendTaskId = ref(null)

const showCancelConfirm = ref(false)
const cancelTarget = ref(null)
const cancelling = ref(false)

const stats = ref({
  scheduled: 0,
  processing: 0,
  completed: 0,
  failed: 0,
  cancelled: 0
})

const statusTabs = [
  { key: 'scheduled', label: '待发送' },
  { key: 'processing', label: '发送中' },
  { key: 'completed', label: '已完成' },
  { key: 'cancelled', label: '已取消' },
  { key: 'failed', label: '失败' },
  { key: 'all', label: '全部' }
]

const currentTabLabel = computed(() => {
  return statusTabs.find(t => t.key === activeTab.value)?.label || ''
})

const isAllSelected = computed(() => {
  return tasks.value.length > 0 && selectedIds.value.length === tasks.value.length
})

let refreshTimer = null

const formatDateTime = (dateString) => {
  if (!dateString) return '—'
  return new Date(dateString).toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  })
}

const getCountdown = (scheduledAt) => {
  const diff = new Date(scheduledAt) - Date.now()
  if (diff <= 0) return '即将发送'
  const hours = Math.floor(diff / 3600000)
  const minutes = Math.floor((diff % 3600000) / 60000)
  if (hours > 24) return `${Math.floor(hours / 24)} 天后`
  if (hours > 0) return `${hours} 小时 ${minutes} 分钟后`
  return `${minutes} 分钟后`
}

const getStatusClass = (status) => {
  const map = {
    scheduled: 'bg-blue-100 text-blue-800',
    pending: 'bg-amber-100 text-amber-800',
    sending: 'bg-amber-100 text-amber-800',
    sent: 'bg-green-100 text-green-800',
    opened: 'bg-green-100 text-green-800',
    clicked: 'bg-green-100 text-green-800',
    cancelled: 'bg-gray-100 text-gray-600',
    failed: 'bg-red-100 text-red-800'
  }
  return map[status] || 'bg-gray-100 text-gray-600'
}

const loadStats = async () => {
  try {
    const data = await emailServiceV2.getReplyStatistics()
    const breakdown = data.status_breakdown || {}
    stats.value = {
      scheduled: breakdown.scheduled || 0,
      processing: (breakdown.pending || 0) + (breakdown.sending || 0),
      completed: (breakdown.sent || 0) + (breakdown.opened || 0) + (breakdown.clicked || 0),
      failed: breakdown.failed || 0,
      cancelled: breakdown.cancelled || 0
    }
  } catch (e) {
    console.error('加载统计失败:', e)
  }
}

const loadTasks = async (page = currentPage.value) => {
  try {
    loading.value = true
    currentPage.value = page
    selectedIds.value = []

    const params = {
      page,
      page_size: pageSize,
      ordering: activeTab.value === 'scheduled' ? '-scheduled_at,-created_at' : '-created_at'
    }
    if (activeTab.value !== 'all') {
      params.status_group = activeTab.value
    }
    if (searchQuery.value.trim()) {
      params.search = searchQuery.value.trim()
    }

    const res = await emailServiceV2.getReplyLogs(params)
    tasks.value = res.results || []
    totalCount.value = res.count || 0
    totalPages.value = Math.ceil(totalCount.value / pageSize) || 1
  } catch (e) {
    toast.error('加载任务列表失败')
    console.error(e)
  } finally {
    loading.value = false
  }
}

const switchTab = (tab) => {
  activeTab.value = tab
  loadTasks(1)
}

const toggleSelectAll = (e) => {
  if (e.target.checked) {
    selectedIds.value = tasks.value.map(t => t.id)
  } else {
    selectedIds.value = []
  }
}

const openEditModal = (task) => {
  editingTaskId.value = task.id
  showEditModal.value = true
}

const closeEditModal = () => {
  showEditModal.value = false
  editingTaskId.value = null
}

const onTaskUpdated = () => {
  closeEditModal()
  loadTasks()
  loadStats()
}

const openResendModal = (task) => {
  resendTaskId.value = task.id
  showResendModal.value = true
}

const closeResendModal = () => {
  showResendModal.value = false
  resendTaskId.value = null
}

const onResendSent = () => {
  closeResendModal()
  loadTasks()
  loadStats()
}

const confirmCancel = (task) => {
  cancelTarget.value = { ...task, batch: false }
  showCancelConfirm.value = true
}

const confirmBatchCancel = () => {
  cancelTarget.value = { batch: true }
  showCancelConfirm.value = true
}

const executeCancel = async () => {
  try {
    cancelling.value = true
    if (cancelTarget.value?.batch) {
      await emailServiceV2.batchCancelScheduledEmails(selectedIds.value)
      toast.success('批量取消成功')
    } else {
      await emailServiceV2.cancelScheduledEmail(cancelTarget.value.id)
      toast.success('定时邮件已取消')
    }
    showCancelConfirm.value = false
    cancelTarget.value = null
    loadTasks()
    loadStats()
  } catch (e) {
    toast.error(e.error || e.message || '取消失败')
  } finally {
    cancelling.value = false
  }
}

onMounted(() => {
  loadTasks()
  loadStats()
  refreshTimer = setInterval(() => {
    if (activeTab.value === 'scheduled' && !showEditModal.value && !showResendModal.value) {
      loadTasks(currentPage.value)
    }
  }, 30000)
})

onUnmounted(() => {
  if (refreshTimer) clearInterval(refreshTimer)
})
</script>
